import momentTz, { Moment } from 'moment-timezone';
import { getTimezone } from '@mgm/common/utils';
import { useAppSelector } from './useAppStore';
import { getSelectedMember } from '../store/sheets/selectors';
import { mergedDateTime } from '../utils/helpers';
import { TBreak, TTimeCardShift } from '../types';

export function useShiftsValidation() {
  const selectedMember = useAppSelector(getSelectedMember);

  const isSequential = (previousBreakEnd: string, currentBreakStart: string) => {
    const previousEnd = momentTz.tz(previousBreakEnd, 'YYYY-MM-DD hh:mm:ss A', getTimezone());
    const currentStart = momentTz.tz(currentBreakStart, 'YYYY-MM-DD hh:mm:ss A', getTimezone());

    return previousEnd.isSameOrBefore(currentStart);
  };

  const isWithinTimeframe = (startTime: string, endTime: string, breakStart: string, breakEnd: string) => {
    const shiftStart = momentTz.tz(startTime, 'YYYY-MM-DD hh:mm:ss A', getTimezone());
    const shiftEnd = momentTz.tz(endTime, 'YYYY-MM-DD hh:mm:ss A', getTimezone());
    const breakStartTime = momentTz.tz(breakStart, 'YYYY-MM-DD hh:mm:ss A', getTimezone());
    const breakEndTime = momentTz.tz(breakEnd, 'YYYY-MM-DD hh:mm:ss A', getTimezone());

    if (breakEndTime.isBefore(breakStartTime)) {
      breakEndTime.add(1, 'day');
    }

    return breakStartTime.isSameOrAfter(shiftStart) && breakEndTime.isSameOrBefore(shiftEnd);
  };

  const validateBreaks = <T extends {
    startDate: Moment,
    date: Moment,
    startTime: Moment,
    endDate: Moment,
    breaks: Array<TBreak>,
    endTime: Moment }>(data: T) => {
    const localStartDate = data.startDate || data.date;
    const localEndDate = data.endDate || data.date;

    const startShiftTime = mergedDateTime(localStartDate, momentTz(data.startTime).format('HH:mm')) as Moment;
    const endShiftTime = mergedDateTime(localEndDate, momentTz(data.endTime).format('HH:mm')) as Moment;

    for (let i = 0; i < data.breaks.length; i += 1) {
      const currentBreak = data.breaks[i];

      if (!currentBreak.breakStart || !currentBreak.breakEnd) {
        return false;
      }

      const breakStart = mergedDateTime(localStartDate, momentTz(currentBreak.breakStart)?.format('HH:mm')) as Moment;
      const breakEnd = mergedDateTime(localEndDate, momentTz(currentBreak.breakEnd)?.format('HH:mm')) as Moment;

      if (!isWithinTimeframe(startShiftTime?.format(), endShiftTime?.format(), breakStart?.format(), breakEnd?.format())) {
        return false;
      }

      if (i < data.breaks.length - 1) {
        const nextBreak = data.breaks[i + 1];
        if (!isSequential(currentBreak.breakEnd, nextBreak.breakStart)) {
          return false;
        }
      }
    }

    return true;
  };

  const isShiftDuplicate = (newShift: TTimeCardShift) => {
    // Helper function to compare breaks
    function areBreaksEqual(breaks1: TBreak[], breaks2: TBreak[]) {
      if (breaks1.length !== breaks2.length) {
        return false;
      }
      for (let i = 0; i < breaks1.length; i += 1) {
        if (
          breaks1[i].breakStart !== breaks2[i].breakStart
          || breaks1[i].breakEnd !== breaks2[i].breakEnd
          || breaks1[i].id !== breaks2[i].id
          || breaks1[i].breakTime !== breaks2[i].breakTime
        ) {
          return false;
        }
      }
      return true;
    }

    // Iterate over each existing shift
    // eslint-disable-next-line no-restricted-syntax
    for (const shift of selectedMember.shifts) {
      // Compare start and end times in UTC
      const newShiftStart = new Date(newShift.start).getTime();
      const newShiftEnd = new Date(newShift.end).getTime();
      const existingShiftStart = new Date(shift.start).getTime();
      const existingShiftEnd = new Date(shift.end).getTime();

      if (newShiftStart === existingShiftStart && newShiftEnd === existingShiftEnd) {
        // Compare location
        if (newShift.location.id === shift.location.id && newShift.location.name === shift.location.name) {
          // Compare breaks
          if (areBreaksEqual(newShift.breaks, shift.breaks)) {
            return true;
          }
        }
      }
    }

    // If no match is found
    return false;
  };

  return {
    validateBreaks,
    isShiftDuplicate,
  };
}
